import { AuthService } from './../../shared/services/auth.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { AutomationService } from 'src/app/shared/services/automation.service';
import { FirebaseService } from './../../shared/services/firebase.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import * as moment from 'moment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import {
  GET_ALL_ORDERS_RESP,
  ServerSidePages,
  Assistant,
} from 'src/app/shared/models';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { PaginationTableHeaders } from 'src/app/shared/models/generic';
import { Router } from '@angular/router';
import { UploadLcdPolicyDocModalComponent } from './upload-lcd-policy/upload-lcd-policy-doc-modal.component';
import { AddLcdPolicyModalComponent } from './add-lcd-policy/add-lcd-policy-modal.component';

const DATE: Date = new Date();

@Component({
  selector: 'app-automation',
  templateUrl: './automation.component.html',
  styleUrls: ['./automation.component.scss'],
})
export class AutomationComponent implements OnInit {
  orderTableHeaders: PaginationTableHeaders[] = [
    { title: 'patient_name', value: 'patient_name', sort: false, orderBy: '' },
    {
      title: 'provider_name',
      value: 'provider_name',
      sort: false,
      orderBy: '',
    },
    {
      title: 'order_summary',
      value: 'order_summary',
      sort: false,
      orderBy: '',
    },
    { title: 'MRN', value: 'MRN', sort: false, orderBy: '' },
    {
      title: 'automation_status',
      value: 'automation_status',
      sort: false,
      orderBy: '',
    },
    { title: 'created_at', value: 'created_at', sort: true, orderBy: '' },
    {
      title: 'auth_status',
      value: 'auth_status',
      sort: false,
      orderBy: '',
    },
    {
      title: 'authorization_status',
      value: 'authorization_status',
      sort: false,
      orderBy: '',
    },
  ];
  assistantInfo: Assistant = null;
  selectedUserOrders: any[];
  selectedUserOrdersTableData: any[];
  selectedOrder: any;
  orderDetails: any;
  assistantUID: string;
  assistantPractice: string;
  selectedStatus: { description: string; status: string };
  tablePages: ServerSidePages;
  pageSize: number = 10;
  searchKeyword: string = '';
  searchCriteria: string = 'patient_name';
  @ViewChild('attachmentSwitch', { static: false })
  attachmentSwitch: MatSlideToggle;
  patient: any;
  conversation: any;
  user: any;
  userRole: string;

  constructor(
    private firebaseService: FirebaseService,
    private automationService: AutomationService,
    private toastr: ToastrService,
    private authService: AuthService,
    private spinnerService: NgxSpinnerService,
    private modalService: NgbModal,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Preload html2pdf library early to keep user gesture context strong when downloading
    this.preloadHtml2Pdf();
    this.init();
  }

  async init(): Promise<void> {
    this.user = JSON.parse(localStorage.getItem('user'));
    if (!!!this.user) this.authService.logout();
    this.userRole = await this.authService.getUserRole();
    this.assistantInfo = await this.firebaseService.getAssistant(
      this.user.uid,
      this.user.displayName
    );
    this.assistantUID = this.user.uid;
    this.assistantPractice = this.user.displayName;
    this.getPendingOrders();
  }

  // Preload html2pdf.js once so downloads are instant on click and less likely to be blocked
  private preloadHtml2Pdf(): void {
    try {
      const w: any = window as any;
      if (w.html2pdf) return; // already loaded
      const existing = document.querySelector(
        'script[data-preload-html2pdf="1"]'
      ) as HTMLScriptElement;
      if (existing) return;
      const s = document.createElement('script');
      s.src =
        'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
      s.crossOrigin = 'anonymous';
      s.referrerPolicy = 'no-referrer';
      s.setAttribute('data-preload-html2pdf', '1');
      document.head.appendChild(s);
    } catch {}
  }

  getPendingOrders(
    page: number = 1,
    limit: number = this.pageSize,
    orderBy: any = {}
  ): void {
    this.spinnerService.show();
    if (this.userRole === 'practice_admin') {
      this.automationService
        .getAllMRIOrders(
          this.assistantPractice,
          page,
          limit,
          orderBy,
          this.searchKeyword,
          this.searchCriteria
        )
        .then((resp: GET_ALL_ORDERS_RESP) => {
          this.tablePages = {
            currentPage: resp.data.currentPage,
            totalPages: Math.trunc(
              (resp.data.total + resp.data.limit - 1) / resp.data.limit
            ),
            lastPage: Math.trunc(
              (resp.data.total + resp.data.limit - 1) / resp.data.limit
            ),
            nextPage: resp.data.nextPage,
            prevPage: resp.data.previousPage,
            firstPage: 1,
            total: resp.data.total,
          };
          this.selectedUserOrders = resp.data.data || [];
          const date = new Date();
          const currentDate = moment(date).startOf('days');
          this.selectedUserOrdersTableData = this.selectedUserOrders.map(
            (order: any) => ({
              id: order.order_id,
              order: order,
              created_by_action_item: order.is_created_by_action_item || false,
              order_summary: order.order_summary,
              ai_order_data: order.ai_order_data,
              ai_order_insurance: order.ai_order_insurance,
              ai_order_portal_data: order.ai_order_portal_data,
              automation_status: {
                ehr:
                  order.ai_order_data &&
                  Object.keys(order.ai_order_data).length > 0 &&
                  order.ai_order_insurance &&
                  Object.keys(order.ai_order_insurance).length > 0,
                portal: order.ai_portal_process_ind,
              },
              authorization_status: order?.ai_order_portal_data?.status
                ? order.ai_order_portal_data.status.toLowerCase()
                : order.ai_ehr_process_error
                ? order.ai_ehr_process_error
                : order.ai_portal_process_error
                ? order.ai_portal_process_error
                : '',
              created_at: order.created_at
                ? moment.parseZone(order.created_at).format('MM/DD/YYYY')
                : '',
              patient: order.patient,
              order_status: order.order_status,
              provider: order.provider,
              patient_name:
                order.patient?.patient_name ||
                `${order.patient?.last_name}, ${order.patient?.first_name}`,
              MRN: order.patient?.med_rec_nbr,
              provider_name: order.provider?.name,
              auth_status: order?.ai_order_portal_data?.status
                ? order.ai_order_portal_data.status.toLowerCase()
                : order.ai_portal_process_error || order.ai_ehr_process_error
                ? 'error'
                : '',
            })
          );
        })
        .catch(() => {
          this.toastr.error('Something went wrong. Please try again');
        })
        .finally(() => this.spinnerService.hide());
    }
  }

  policyDocuments = async (record: any): Promise<void> => {
    const cptCode = record?.ai_order_data?.order_cpt_code;
    const insurance = record?.ai_order_insurance?.insurance_plan;
    const patientId = record?.patient?.id;
    const orderId = record.id;
    if (!cptCode && !insurance) {
      this.toastr.error('Order is not processed yet. Please try again later.');
      return;
    }

    if (!cptCode) {
      this.toastr.error('CPT code does not exist for this record.');
      return;
    }
    if (!insurance) {
      this.toastr.error('Insurance does not exist for this record.');
      return;
    }
    if (!patientId) {
      this.toastr.error('Patient ID does not exist for this record.');
      return;
    }
    if (!orderId) {
      this.toastr.error('Order ID does not exist for this record.');
      return;
    }
    const newCPT = JSON.parse(cptCode);
    this.spinnerService.show();
    const ortMasters = await this.automationService.getLastSixMonthsOrtMasters(
      patientId
    );
    this.spinnerService.hide();
    if (
      ortMasters &&
      ortMasters.data &&
      ortMasters.data.ortMasters &&
      ortMasters.data.ortMasters.length > 0
    ) {
      const params = new URLSearchParams({
        cptCode: newCPT[0] || '',
        insurance: insurance || '',
        orderId: orderId || '',
      });
      window.open(
        `/dashboard/lcd-policy-document?${params.toString()}`,
        '_blank'
      );
    } else {
      this.toastr.error(
        'No Medical Records found for this patient in last six months.'
      );
    }
  };

  filterUsers(event: KeyboardEvent, clearSearch?: boolean): void {
    if (clearSearch && event.code === 'Enter') this.searchKeyword = '';

    let order,
      status = {};
    this.orderTableHeaders.forEach((header) => {
      if (header.orderBy) {
        order = {
          column_name: header.value,
          sort: header.orderBy,
        };
      }
    });
    this.getPendingOrders(1, this.pageSize, order);
  }

  lcdPolicyDocuments(): void {
    window.open('/dashboard/lcd-policy-document', '_blank');
  }

  uploadLCDPolicyDocument(): void {
    this.modalService.open(UploadLcdPolicyDocModalComponent, {
      size: 'xl',
      animation: true,
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });
  }

  addLCDPolicy(): void {
    this.modalService.open(AddLcdPolicyModalComponent, {
      size: 'lg',
      animation: true,
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });
  }

  onChangeSearchCriteria(event: any): void {
    this.searchCriteria = event.target.value;
  }

  downloadLCDPolicyDocument = async (record: any): Promise<void> => {
    try {
      this.spinnerService.show();

      const cptCode = record?.ai_order_data?.order_cpt_code;
      const insurance = record?.ai_order_insurance?.insurance_plan;
      const patientId = record?.patient?.id;
      const orderId = record.id;
      const aiResponseOrderData = record?.ai_order_data?.lcd_coverage_details;

      if (!cptCode || !insurance || !patientId || !orderId) {
        this.toastr.error('Missing required record data.');
        return;
      }

      if (!aiResponseOrderData || aiResponseOrderData.length < 10) {
        this.toastr.error('AI response does not exist for this record.');
        return;
      }

      const newCPT = JSON.parse(cptCode);
      const ortResp = await this.automationService.getLastSixMonthsOrtMasters(
        patientId
      );
      const ortMasters: any[] = ortResp?.data?.ortMasters || [];

      if (!ortMasters.length) {
        this.toastr.error('No Medical Records found for this patient.');
        return;
      }

      const safe = (val: any) =>
        val === undefined || val === null ? '' : String(val);
      const escapeHtml = (s: string) =>
        s.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const formatDate = (d: any): string => {
        try {
          if (!d) return '';
          const date = new Date(d);
          if (isNaN(+date)) return '';
          return date.toLocaleDateString();
        } catch {
          return '';
        }
      };

      const patient = record?.patient || {};
      const mrn = safe(patient?.med_rec_nbr || patient?.MRN);
      const name = safe(
        patient?.patient_name ||
          `${safe(patient?.first_name)} ${safe(patient?.last_name)}`.trim()
      );
      const memberId = safe(record?.ai_order_insurance?.member_id);
      const payer = safe(insurance);
      const aiResponse = safe(record?.ai_order_data?.lcd_coverage_details);
      const cpt = safe(Array.isArray(newCPT) ? newCPT[0] : newCPT);

      const styles = `
      <style>
        body {
          font-family: 'Helvetica', Arial, sans-serif;
          background: #fff;
          color: #212529;
          line-height: 1.6;
          font-size: 16px;
        }
        .pdf-container {
          width: 794px;
          margin: 0 auto;
          padding: 25px;
          box-sizing: border-box;
        }
        h5.section-title {
          font-size: 24px;
          font-weight: 700;
          color: #000000ff;
          border-bottom: 3px solid #000000ff;
          padding-bottom: 6px;
          margin-bottom: 18px;
        }
        h6 {
          font-size: 19px;
          color:  #000000ff;
          margin-top: 20px;
          margin-bottom: 10px;
        }
        .patient-meta {
          font-size: 16px;
          display: flex;
          flex-wrap: wrap;
          gap: 18px;
          margin-bottom: 18px;
        }
        pre {
          white-space: pre-wrap;
          word-break: break-word;
          font-size: 13px;
          color: #333;
          font-family: Arial, sans-serif;
          line-height: 1.6;
          margin-top: 8px;
        }
        .ort-item {
          padding: 15px;
        }
        .ort-title {
          font-weight: 600;
          font-size: 17px;
          color: #0d6efd;
          margin-bottom: 10px;
        }
      </style>
    `;

      const filename = `${
        [name, cpt, payer].filter(Boolean).join('_').replace(/\s+/g, '_') ||
        'document'
      }_coverage.pdf`;

      const pdf = new jsPDF('p', 'pt', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const renderSectionToPDF = async (
        element: HTMLElement,
        addNewPage = false
      ) => {
        const canvas = await html2canvas(element, { scale: 2, useCORS: true });
        const imgData = canvas.toDataURL('image/jpeg', 0.98);
        const imgWidth = pdfWidth;
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        if (addNewPage) pdf.addPage();

        const marginBottom = 20;

        while (heightLeft > 0) {
          pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
          heightLeft -= pdfHeight - marginBottom;
          position -= pdfHeight - marginBottom;
          if (heightLeft > 0) pdf.addPage();
        }
      };

      // ----- MAIN SUMMARY -----
      const mainDiv = document.createElement('div');
      mainDiv.style.position = 'fixed';
      mainDiv.style.left = '-9999px';
      mainDiv.innerHTML = `
      ${styles}
      <div class="pdf-container">
        <h5 class="section-title">Patient Information</h5>
        <div class="patient-meta">
          ${mrn ? `<span><strong>MRN:</strong> ${mrn}</span>` : ''}
          ${name ? `<span><strong>Name:</strong> ${name}</span>` : ''}
          ${
            memberId
              ? `<span><strong>Member ID:</strong> ${memberId}</span>`
              : ''
          }
          ${payer ? `<span><strong>Payer:</strong> ${payer}</span>` : ''}
        </div>
        <h6>Medical Necessity Summary</h6>
        <div style="font-family: Arial, sans-serif; line-height: 1.6; font-size: 13px; color: #333;">
          <pre>${escapeHtml(aiResponse)}</pre>
        </div>
      </div>
    `;
      document.body.appendChild(mainDiv);

      await renderSectionToPDF(mainDiv, false);
      document.body.removeChild(mainDiv);

      // ----- EACH ORT MASTER -----
      const marginLeft = 40; // left margin in pt
      const marginRight = 40; // right margin in pt
      const marginTop = 40; // top margin in pt
      const marginBottom = 40; // bottom margin in pt
      const availableWidth = pdfWidth - marginLeft - marginRight;

      // Helper: convert PDF pt -> CSS px (approx, based on 96dpi and 72pt/in)
      const ptToPx = (pt: number) => Math.round((pt * 96) / 72);

      // Track whether we should add a new page before placing images
      let startedOrtPages = false;

      for (let i = 0; i < ortMasters.length; i++) {
        const ort = ortMasters[i];
        const htmlContent = ort?.master_notes || '';

        // Create a temporary container for HTML and set width in px to match PDF available width
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'fixed';
        tempDiv.style.left = '-9999px';
        tempDiv.style.top = '0';
        // use px width that corresponds to PDF pt width
        const availableWidthPx = ptToPx(availableWidth);
        tempDiv.style.width = `${availableWidthPx}px`;
        tempDiv.innerHTML = htmlContent;
        document.body.appendChild(tempDiv);

        // Use device pixel ratio-aware scale for crisp text, but cap to avoid huge memory usage
        const deviceScale = Math.min(window.devicePixelRatio || 1, 2);

        // px <-> pt helpers that account for the chosen canvas scale (use floats to reduce rounding error)
        const ptToPxScaled = (pt: number) => (pt * 96) / 72 * deviceScale;
        const pxToPtScaled = (px: number) => (px * 72) / 96 / deviceScale;

        // compute line height in px from the element's computed line-height so we can reserve a whole line
        let lineHeightPx = Math.ceil(24 * deviceScale);
        try {
          const cs = window.getComputedStyle(tempDiv);
          const lh = cs.lineHeight;
          if (lh && lh.indexOf('px') > -1) {
            lineHeightPx = Math.ceil(parseFloat(lh) * deviceScale);
          } else {
            // fallback to font-size * 1.2
            const fs = cs.fontSize && cs.fontSize.indexOf('px') > -1 ? parseFloat(cs.fontSize) : 14;
            lineHeightPx = Math.ceil(fs * 1.2 * deviceScale);
          }
        } catch (err) {
          // keep default lineHeightPx
        }

        // use a smaller overlap (fraction of line height) to avoid duplication but still prevent splits
        const overlapPx = Math.max(4, Math.ceil(lineHeightPx * 0.25));

        // Render HTML to canvas at deviceScale so fonts rasterize cleanly
        const canvas = await html2canvas(tempDiv, {
          scale: deviceScale,
          useCORS: true,
          logging: false,
        });

        const canvasWidth = canvas.width; // px (includes scale)
        const canvasHeight = canvas.height; // px (includes scale)

        // Compute PDF page height available (pt) and convert to canvas px units for slicing
        const pageHeightPt = pdfHeight - marginTop - marginBottom; // pt
        const pageHeightPx = Math.floor(ptToPxScaled(pageHeightPt)); // px in canvas space

        // PDF image width in pt
        const imgWidthPt = availableWidth;

        // Start a fresh page for ORT masters so they don't overlap summary content
        pdf.addPage();

        // Slice the canvas vertically into page-sized chunks and add each as a PDF page
        let y = 0;
        let isFirstChunk = true;
        while (y < canvasHeight) {
          // compute slice start and height. For the first chunk we start at y; for subsequent chunks
          // we'll include overlap above the slice so we can avoid splitting lines, but we'll crop the
          // bottom overlap from the page we add so content is not duplicated.

          const remaining = canvasHeight - y;
          let sliceStart = y;
          // reserve one full line at the bottom of the page so it moves entirely to the next page
          const reserveLinePx = lineHeightPx;
          // content height we want on the page (exclude reserved last line)
          const contentHeightPx = Math.max(0, pageHeightPx - reserveLinePx);
          let sliceHeight = Math.min(contentHeightPx, remaining);

          const willHaveMore = remaining > sliceHeight;
          if (willHaveMore) {
            // include small overlap for safety
            sliceHeight = Math.min(sliceHeight + overlapPx, remaining);
          }

          // create a temporary canvas for the page chunk (in canvas px space)
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = canvasWidth;
          pageCanvas.height = sliceHeight;
          const ctx = pageCanvas.getContext('2d');
          if (ctx) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
            ctx.drawImage(canvas, 0, sliceStart, canvasWidth, sliceHeight, 0, 0, canvasWidth, sliceHeight);
          }

          // If there will be more content after this page, crop the bottom overlap portion so it isn't duplicated
          let canvasToAdd = pageCanvas;
          if (willHaveMore && overlapPx > 0) {
            const cropHeight = pageCanvas.height - overlapPx; // remove bottom overlap
            const cropped = document.createElement('canvas');
            cropped.width = pageCanvas.width;
            cropped.height = Math.max(0, cropHeight);
            const cctx = cropped.getContext('2d');
            if (cctx) {
              cctx.fillStyle = '#ffffff';
              cctx.fillRect(0, 0, cropped.width, cropped.height);
              cctx.drawImage(pageCanvas, 0, 0, pageCanvas.width, cropHeight, 0, 0, cropped.width, cropped.height);
            }
            canvasToAdd = cropped;
          }

          const imgData = canvasToAdd.toDataURL('image/jpeg', 0.98);

          // natural image height in PDF pts if width is imgWidthPt (maintain aspect ratio)
          const naturalImgHeightPt = (canvasToAdd.height * imgWidthPt) / canvasToAdd.width;

          // PDF page content height available (pt)
          const pageContentHeightPt = pdfHeight - marginTop - marginBottom;

          // If natural image height exceeds page content height, scale down to fit and adjust width accordingly
          let drawWidthPt = imgWidthPt;
          let drawHeightPt = naturalImgHeightPt;
          if (naturalImgHeightPt > pageContentHeightPt) {
            const scaleDown = pageContentHeightPt / naturalImgHeightPt;
            drawHeightPt = pageContentHeightPt;
            drawWidthPt = imgWidthPt * scaleDown;
          }

          // For the very first chunk we already added a separator page above; subsequent chunks need new pages
          if (!isFirstChunk) pdf.addPage();

          // center narrower images horizontally if we scaled width down
          const drawX = marginLeft + Math.max(0, (imgWidthPt - drawWidthPt) / 2);

          pdf.addImage(imgData, 'JPEG', drawX, marginTop, drawWidthPt, drawHeightPt);

          // advance y: if we added overlap, step forward by the non-overlapped height
          if (willHaveMore && overlapPx > 0) y += sliceHeight - overlapPx;
          else y += sliceHeight;

          isFirstChunk = false;
        }

        document.body.removeChild(tempDiv);
      }

      pdf.save(filename);
    } catch (e) {
      console.error('PDF generation failed', e);
      this.toastr.error('Failed to generate PDF. Please try again.');
    } finally {
      this.spinnerService.hide();
    }
  };

  orderBy = (column_name: string): void => {
    const index = this.orderTableHeaders.findIndex(
      (header) => header.value === column_name
    );
    let status = {};

    this.orderTableHeaders.forEach((header) => {
      if (header.value != column_name) header.orderBy = '';
    });

    if (this.orderTableHeaders[index].orderBy === 'ASC')
      this.orderTableHeaders[index].orderBy = 'DESC';
    else this.orderTableHeaders[index].orderBy = 'ASC';

    if (this.selectedStatus && this.selectedStatus.status) {
      status = this.selectedStatus;
    }

    const order = {
      column_name,
      sort: this.orderTableHeaders[index].orderBy,
    };

    this.getPendingOrders(this.tablePages.currentPage, this.pageSize, order);
  };

  reloadTable = ($event: boolean = false): void => {
    if ($event === true) {
      let order,
        status = {};
      this.orderTableHeaders.forEach((header) => {
        if (header.orderBy) {
          order = {
            column_name: header.value,
            sort: header.orderBy,
          };
        }
      });
      if (this.tablePages.currentPage)
        this.getPendingOrders(
          this.tablePages.currentPage,
          this.pageSize,
          order
        );
    }
  };

  selectOrder = (order: any): void => {
    if (!order) {
      this.selectedOrder = null;
      return;
    }
    this.selectedOrder = order;
    this.orderDetails = order.order;
    // console.log('Selected Order:', order.order);
  };

  lastPage = (): void => {
    let order,
      status = {};
    this.orderTableHeaders.forEach((header) => {
      if (header.orderBy) {
        order = {
          column_name: header.value,
          sort: header.orderBy,
        };
      }
    });
    if (this.tablePages.nextPage)
      this.getPendingOrders(this.tablePages.lastPage, this.pageSize, order);
  };

  nextPage = (): void => {
    let order,
      status = {};
    this.orderTableHeaders.forEach((header) => {
      if (header.orderBy) {
        order = {
          column_name: header.value,
          sort: header.orderBy,
        };
      }
    });
    if (this.tablePages.nextPage)
      this.getPendingOrders(this.tablePages.nextPage, this.pageSize, order);
  };

  prevPage = (): void => {
    let order;
    this.orderTableHeaders.forEach((header) => {
      if (header.orderBy) {
        order = {
          column_name: header.value,
          sort: header.orderBy,
        };
      }
    });
    if (this.tablePages.prevPage)
      this.getPendingOrders(this.tablePages.prevPage, this.pageSize, order);
  };

  firstPage = (): void => {
    let order;
    this.orderTableHeaders.forEach((header) => {
      if (header.orderBy) {
        order = {
          column_name: header.value,
          sort: header.orderBy,
        };
      }
    });
    if (this.tablePages.prevPage)
      this.getPendingOrders(this.tablePages.firstPage, this.pageSize, order);
  };

  customPage = (page: string): void => {
    const pageNumber = parseInt(page);
    if (pageNumber === this.tablePages.currentPage) {
      this.toastr.info('Page already selected.');
      return;
    } else if (
      typeof pageNumber === 'number' &&
      pageNumber <= this.tablePages.totalPages &&
      0 < pageNumber
    ) {
      let order;
      this.orderTableHeaders.forEach((header) => {
        if (header.orderBy) {
          order = {
            column_name: header.value,
            sort: header.orderBy,
          };
        }
      });
      this.getPendingOrders(pageNumber, this.pageSize, order);
    } else
      this.toastr.error(
        'Please enter a page number between the range of ' +
          this.tablePages.firstPage +
          ' to ' +
          this.tablePages.lastPage
      );
  };
}
